// "Users" tab of the admin tool: list every sign-in account together with its
// users/{uid} record, and change password, e-mail, name, role / user type,
// verified + disabled flags, or delete the account. Everything goes through
// the server's /api/users/* routes, which act as the Firebase service account
// (a browser session can't change another user's password).
//
// Without the service account on the server ("direct" mode) the tab falls
// back to reading users/ with the admin's own login, like the places tabs do:
// the database fields (role, user type, …) stay editable, the sign-in account
// parts (password, e-mail, disabled, delete) are unavailable.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ref, get, update } from 'firebase/database';
import { auth, db } from './firebase';

interface UserRow {
  uid: string;
  email: string;
  displayName: string;
  disabled: boolean;
  emailVerified: boolean;
  createdAt: number;
  lastLoginAt: number;
  providers: string[];
  hasAuth: boolean;
  profile: Record<string, string | number | boolean>;
  nested: string[];
}

interface AuthChanges {
  email?: string;
  password?: string;
  displayName?: string;
  disabled?: boolean;
  emailVerified?: boolean;
}

// Always offered in the role picker, next to whatever roles the data has.
const BASE_ROLES = ['admin', 'user', 'trial', 'premium'];

// Paid / trial roles need their period on the record — the app's reminders go
// by these two fields, stored as "YYYY-MM-DD" text.
const SUB_ROLES = ['trial', 'premium'];
const SUB_START = 'subscriptionPurchaseDate';
const SUB_END = 'subscriptionExpirationDate';
const isSubRole = (r: string) => SUB_ROLES.includes(r.trim().toLowerCase());
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// "YYYY-MM-DD" + n days / months / years, in local time.
function addPeriod(start: string, n: number, unit: 'd' | 'm' | 'y') {
  const [y, m, d] = start.split('-').map(Number);
  const t = new Date(y, m - 1, d);
  if (unit === 'd') t.setDate(t.getDate() + n);
  if (unit === 'm') t.setMonth(t.getMonth() + n);
  if (unit === 'y') t.setFullYear(t.getFullYear() + n);
  return ymd(t);
}

async function api(path: string, body?: any) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in');
  const r = await fetch(path, {
    method: body ? 'POST' : 'GET',
    headers: { Authorization: `Bearer ${idToken}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { status: r.status });
  return j;
}

// Record fields the app may keep the e-mail / name under, in order of preference.
const EMAIL_KEYS = ['email', 'userEmail', 'mail'];
const NAME_KEYS = ['displayName', 'name', 'username', 'userName', 'nickname', 'fullName'];
const pick = (p: Record<string, any>, keys: string[]) => {
  for (const k of keys) if (typeof p[k] === 'string' && p[k]) return p[k] as string;
  return '';
};
// The in-app username, falling back to the sign-in account's display name.
const USERNAME_KEYS = ['username', 'userName', 'nickname'];
// users/ read straight from the database — the fallback when the server has
// no service account. Nothing is known about the sign-in accounts here.
async function loadDirect(): Promise<UserRow[]> {
  const snap = await get(ref(db, 'users'));
  return Object.entries<any>(snap.val() || {}).map(([uid, v]) => {
    const profile: UserRow['profile'] = {};
    const nested: string[] = [];
    for (const [k, x] of Object.entries<any>(v && typeof v === 'object' ? v : {})) {
      if (x !== null && typeof x === 'object') nested.push(k);
      else profile[k] = x;
    }
    return {
      uid, email: pick(profile, EMAIL_KEYS), displayName: pick(profile, NAME_KEYS),
      disabled: false, emailVerified: false, createdAt: 0, lastLoginAt: 0, providers: [],
      hasAuth: true, profile, nested,
    };
  });
}

const fmtTs = (ms: number) => (ms ? new Date(ms).toLocaleString() : '—');
const PROVIDER_NAMES: Record<string, string> = {
  password: 'e-mail',
  'google.com': 'Google',
  'apple.com': 'Apple',
  'facebook.com': 'Facebook',
  phone: 'phone',
};

function randomPassword(len = 12) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const buf = new Uint32Array(len);
  crypto.getRandomValues(buf);
  return Array.from(buf, (n) => chars[n % chars.length]).join('');
}

const UsersView: React.FC<{ mode: 'demo' | 'production' }> = ({ mode }) => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState<{ msg: string; cls: string }>({ msg: 'Loading users…', cls: '' });
  const [filter, setFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editUid, setEditUid] = useState<string | null>(null);
  // Role picked in the table that needs dates first — the editor opens with it.
  const [editRole, setEditRole] = useState<string | undefined>(undefined);
  // true = no service account on the server; users/ is read and written directly.
  const [direct, setDirect] = useState(false);
  const me = auth.currentUser?.uid;
  const live = mode === 'production';

  const load = useCallback(async () => {
    setLoading(true);
    setMsg({ msg: 'Loading users…', cls: '' });
    try {
      let list: UserRow[];
      let viaDb = false;
      let oldServer = false;
      try {
        list = (await api('/api/users')).users || [];
      } catch (e: any) {
        // 503 = no service account; 404 = a server.js that predates /api/users.
        if (e?.status !== 503 && e?.status !== 404) throw e;
        oldServer = e.status === 404;
        list = await loadDirect();
        viaDb = true;
      }
      list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0) || a.email.localeCompare(b.email));
      setUsers(list);
      setDirect(viaDb);
      setLoaded(true);
      setMsg({
        msg: oldServer
          ? `Loaded ${list.length} user record(s) from the database. The server has no /api/users route — deploy the current server.js and restart it to manage passwords, e-mail, disable and delete.`
          : viaDb
          ? `Loaded ${list.length} user record(s) from the database. Password, e-mail, disable and delete need the service-account key on the server (FIREBASE_SERVICE_ACCOUNT_FILE in .env.server).`
          : `Loaded ${list.length} user(s).`,
        cls: viaDb ? '' : 'ok',
      });
    } catch (e: any) {
      setMsg({ msg: 'Could not load users: ' + (e?.message || e), cls: 'err' });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roles = useMemo(() => {
    const seen = users.map((u) => u.profile.role).filter((r): r is string => typeof r === 'string' && !!r);
    return Array.from(new Set([...BASE_ROLES, ...seen])).sort();
  }, [users]);

  // Writes one user's changes and patches the row locally on success.
  const save = async (uid: string, authChanges: AuthChanges, profile: Record<string, any>, what: string) => {
    if (!live) {
      setMsg({ msg: 'Demo mode — NOT saved. Switch to PRODUCTION (top right) to moderate users.', cls: 'err' });
      return false;
    }
    try {
      if (direct) {
        if (Object.keys(authChanges).length)
          throw new Error('password, e-mail and account flags need the service-account key on the server');
        if (uid === me && 'role' in profile && profile.role !== 'admin')
          throw new Error('you cannot remove your own admin role');
        const updates: Record<string, any> = {};
        for (const [k, v] of Object.entries(profile)) updates[`users/${uid}/${k}`] = v;
        await update(ref(db), updates);
      } else {
        await api('/api/users/update', { uid, auth: authChanges, profile });
      }
      setUsers((prev) =>
        prev.map((u) => {
          if (u.uid !== uid) return u;
          const { password, ...visible } = authChanges;
          const nextProfile = { ...u.profile };
          for (const [k, v] of Object.entries(profile)) {
            if (v === null) delete nextProfile[k];
            else nextProfile[k] = v;
          }
          return { ...u, ...visible, profile: nextProfile };
        })
      );
      setMsg({ msg: `Saved ${what}.`, cls: 'ok' });
      return true;
    } catch (e: any) {
      setMsg({ msg: `Save failed: ${e?.message || e}`, cls: 'err' });
      return false;
    }
  };

  const setRole = async (u: UserRow, role: string) => {
    if ((u.profile.role ?? '') === role) return;
    // Trial / premium can't be set without the subscription dates.
    if (isSubRole(role)) {
      setEditRole(role);
      setEditUid(u.uid);
      return;
    }
    const who = u.email || u.uid;
    if (!window.confirm(`Change the role of ${who} to "${role || '(none)'}"?`)) return;
    await save(u.uid, {}, { role: role || null }, `role of ${who}`);
  };

  const toggleDisabled = async (u: UserRow) => {
    const who = u.email || u.uid;
    const verb = u.disabled ? 'Enable' : 'Disable';
    if (!window.confirm(`${verb} ${who}? ${u.disabled ? '' : 'They will not be able to sign in.'}`)) return;
    await save(u.uid, { disabled: !u.disabled }, {}, `${verb.toLowerCase()}d ${who}`);
  };

  const deleteUser = async (u: UserRow, deleteData: boolean) => {
    if (!live) {
      setMsg({ msg: 'Demo mode — switch to PRODUCTION (top right) to delete users.', cls: 'err' });
      return;
    }
    const who = u.email || u.uid;
    if (
      !window.confirm(
        `Permanently DELETE the account ${who}?${
          deleteData ? '\n\nIts users/ record (role, saved settings) is deleted too.' : ''
        }\n\nLocations this user added stay in the database.`
      )
    )
      return;
    try {
      await api('/api/users/delete', { uid: u.uid, deleteData });
      setUsers((prev) =>
        deleteData ? prev.filter((x) => x.uid !== u.uid) : prev.map((x) => (x.uid === u.uid ? { ...x, hasAuth: false } : x))
      );
      setEditUid(null);
      setMsg({ msg: `Deleted ${who}.`, cls: 'ok' });
    } catch (e: any) {
      setMsg({ msg: `Delete failed: ${e?.message || e}`, cls: 'err' });
    }
  };

  const f = filter.trim().toLowerCase();
  const shown = users.filter((u) => {
    if (roleFilter === '-' ? u.profile.role : roleFilter && u.profile.role !== roleFilter) return false;
    if (!f) return true;
    return [u.email, u.displayName, u.uid, ...Object.values(u.profile).map(String)].some((s) =>
      (s || '').toLowerCase().includes(f)
    );
  });
  const editing = users.find((u) => u.uid === editUid) || null;

  return (
    <div className="managewrap">
      <div className="card">
        <div className="bar">
          <h2 style={{ margin: 0 }}>Users</h2>
          <button className="blue sm" disabled={loading} onClick={load}>
            {loading ? 'Loading…' : '⟳ Reload'}
          </button>
          <label style={{ margin: 0 }}>role</label>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ maxWidth: 150 }}>
            <option value="">Any role</option>
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            <option value="-">(no role)</option>
          </select>
          <span className="count">
            <b>{shown.length}</b>
            {shown.length !== users.length ? ` of ${users.length}` : ''} user(s)
          </span>
          <span className="spacer" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter by e-mail / name / uid / any field…"
            style={{ maxWidth: 280 }}
          />
        </div>
        <div className={'status ' + msg.cls} style={{ marginTop: 8 }}>
          {msg.msg}
          {!live && ' · DEMO mode: changes are disabled.'}
        </div>
        <div className="hint">
          Role changes and enable/disable apply straight from the table — except trial / premium, which open “Edit”
          to set the subscription start and end dates first (the app's reminders go by them). “Edit” changes the password, e-mail, name,
          verified flag and every other field of the user's record (e.g. a user type). Changing the password or
          e-mail signs the user out of their other sessions.
        </div>
      </div>

      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="tablewrap" style={{ maxHeight: 'none', flex: 1, minHeight: 0 }}>
          <table>
            <thead>
              <tr>
                <th className="users-act" />
                <th>e-mail</th>
                <th>username</th>
                <th>role</th>
                <th>sign-in</th>
                <th>status</th>
                <th>created</th>
                <th>last login</th>
              </tr>
            </thead>
            <tbody>
              {loaded && !shown.length && (
                <tr>
                  <td colSpan={8} style={{ color: '#6b7680' }}>
                    No users match.
                  </td>
                </tr>
              )}
              {shown.map((u) => {
                const username = pick(u.profile, USERNAME_KEYS) || u.displayName;
                const fullName = u.displayName && u.displayName !== username ? u.displayName : '';
                return (
                  <tr key={u.uid} className={u.disabled || !u.hasAuth ? 'up' : ''}>
                    <td className="users-act">
                      <button
                        className="sm"
                        onClick={() => {
                          setEditRole(undefined);
                          setEditUid(u.uid);
                        }}
                      >
                        ✎ Edit
                      </button>
                    </td>
                    <td>
                      {u.email || <i style={{ color: '#6b7680' }}>no e-mail</i>}
                      {u.uid === me && <span className="users-you">you</span>}
                      {u.email && !u.emailVerified && !direct && (
                        <div style={{ fontSize: 11, color: '#6b7680' }}>not verified</div>
                      )}
                      <div className="users-uid">{u.uid}</div>
                    </td>
                    <td>
                      {username ? <b>{username}</b> : '—'}
                      {fullName && <div style={{ fontSize: 11, color: '#6b7680' }}>{fullName}</div>}
                    </td>
                    <td>
                      <select
                        value={String(u.profile.role ?? '')}
                        disabled={!live || u.uid === me}
                        title={u.uid === me ? 'You cannot change your own role' : undefined}
                        onChange={(e) => setRole(u, e.target.value)}
                        className={u.profile.role === 'admin' ? 'users-admin' : ''}
                      >
                        <option value="">(none)</option>
                        {roles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{u.providers.map((p) => PROVIDER_NAMES[p] || p).join(', ') || '—'}</td>
                    <td>
                      {direct ? (
                        <span style={{ color: '#6b7680' }} title="Needs the service-account key on the server">—</span>
                      ) : !u.hasAuth ? (
                        <span style={{ color: '#c00' }}>no account</span>
                      ) : (
                        <button
                          className={'sm ' + (u.disabled ? '' : 'danger')}
                          disabled={!live || u.uid === me}
                          onClick={() => toggleDisabled(u)}
                          title={u.disabled ? 'Allow this user to sign in again' : 'Block this user from signing in'}
                        >
                          {u.disabled ? '⛔ disabled — enable' : 'active — disable'}
                        </button>
                      )}
                    </td>
                    <td>{fmtTs(u.createdAt)}</td>
                    <td>{fmtTs(u.lastLoginAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <UserEditor
          key={editing.uid}
          u={editing}
          direct={direct}
          roles={roles}
          initialRole={editRole}
          isMe={editing.uid === me}
          live={live}
          onClose={() => {
            setEditUid(null);
            setEditRole(undefined);
          }}
          onSave={async (a, p) => {
            if (await save(editing.uid, a, p, `changes to ${editing.email || editing.uid}`)) {
              setEditUid(null);
              setEditRole(undefined);
            }
          }}
          onDelete={(deleteData) => deleteUser(editing, deleteData)}
        />
      )}
    </div>
  );
};

/* ---------- edit dialog ---------- */
type FieldType = 'string' | 'number' | 'boolean';
interface FieldDraft {
  key: string;
  type: FieldType;
  value: string;
  orig?: string; // key it was loaded under, so renames delete the old one
}

const toDraft = (key: string, v: string | number | boolean): FieldDraft => ({
  key,
  type: typeof v as FieldType,
  value: String(v),
  orig: key,
});

const UserEditor: React.FC<{
  u: UserRow;
  direct: boolean;
  roles: string[];
  initialRole?: string; // role picked in the table, not saved yet
  isMe: boolean;
  live: boolean;
  onClose: () => void;
  onSave: (a: AuthChanges, profile: Record<string, any>) => Promise<void>;
  onDelete: (deleteData: boolean) => void;
}> = ({ u, direct, roles, initialRole, isMe, live, onClose, onSave, onDelete }) => {
  const [email, setEmail] = useState(u.email);
  const [displayName, setDisplayName] = useState(u.displayName);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [emailVerified, setEmailVerified] = useState(u.emailVerified);
  const [disabled, setDisabled] = useState(u.disabled);
  const origRole = String(u.profile.role ?? '');
  const [role, setRole] = useState(initialRole ?? origRole);
  // The subscription dates get their own date pickers, not generic fields.
  const origStart = String(u.profile[SUB_START] ?? '');
  const origEnd = String(u.profile[SUB_END] ?? '');
  const [subStart, setSubStart] = useState(origStart);
  const [subEnd, setSubEnd] = useState(origEnd);
  const [fields, setFields] = useState<FieldDraft[]>(() =>
    Object.entries(u.profile)
      .filter(([k]) => k !== 'role' && k !== SUB_START && k !== SUB_END)
      .map(([k, v]) => toDraft(k, v))
  );
  const subRole = isSubRole(role);
  const roleChanged = role.trim() !== origRole;
  // Switching into trial / premium means a new period: both dates must be
  // set anew. Otherwise they only have to be there.
  const startMissing = subRole && (!subStart || (roleChanged && subStart === origStart));
  const endMissing = subRole && (!subEnd || (roleChanged && subEnd === origEnd));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleteData, setDeleteData] = useState(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patchField = (i: number, p: Partial<FieldDraft>) =>
    setFields((prev) => prev.map((f, j) => (j === i ? { ...f, ...p } : f)));

  const submit = async () => {
    setErr('');
    const a: AuthChanges = {};
    if (u.hasAuth && !direct) {
      if (email.trim() !== u.email) a.email = email.trim();
      if (displayName.trim() !== u.displayName) a.displayName = displayName.trim();
      if (password) {
        if (password.length < 6) return setErr('The password must be at least 6 characters.');
        a.password = password;
      }
      if (emailVerified !== u.emailVerified) a.emailVerified = emailVerified;
      if (disabled !== u.disabled) a.disabled = disabled;
    }

    const profile: Record<string, any> = {};
    if (roleChanged) profile.role = role.trim() || null;
    if (subRole) {
      const what = `The "${role.trim()}" role`;
      if (startMissing || endMissing)
        return setErr(
          `${what} needs the subscription ${[startMissing && 'start', endMissing && 'end'].filter(Boolean).join(' and ')} date${
            startMissing && endMissing ? 's' : ''
          }${roleChanged ? ' set for this new period' : ''} — the app's reminders go by them.`
        );
      if (subEnd <= subStart) return setErr('The subscription must end after it starts.');
      if (roleChanged && subEnd < ymd(new Date())) return setErr('The subscription end date is already in the past.');
    }
    if (subStart !== origStart) profile[SUB_START] = subStart || null;
    if (subEnd !== origEnd) profile[SUB_END] = subEnd || null;
    const keys = new Set<string>();
    for (const f of fields) {
      const key = f.key.trim();
      if (!key) return setErr('Every field needs a name.');
      if (/[.$#[\]/]/.test(key)) return setErr(`Field name "${key}" can't contain . $ # [ ] /`);
      if (key === 'role' || key === SUB_START || key === SUB_END || keys.has(key))
        return setErr(`Field "${key}" appears twice.`);
      keys.add(key);
      let v: any = f.value;
      if (f.type === 'number') {
        v = Number(f.value);
        if (f.value.trim() === '' || Number.isNaN(v)) return setErr(`"${key}" must be a number.`);
      }
      if (f.type === 'boolean') v = f.value === 'true';
      if (f.orig && f.orig !== key) profile[f.orig] = null;
      if (!f.orig || f.orig !== key || u.profile[key] !== v) profile[key] = v;
    }
    // Fields removed in the dialog.
    for (const k of Object.keys(u.profile))
      if (k !== 'role' && k !== SUB_START && k !== SUB_END && !keys.has(k) && !(k in profile)) profile[k] = null;

    if (!Object.keys(a).length && !Object.keys(profile).length) return onClose();
    if (a.password && !window.confirm(`Set a new password for ${u.email || u.uid}?`)) return;
    setBusy(true);
    await onSave(a, profile);
    setBusy(false);
  };

  return (
    <div className="users-modal-bg" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="users-modal card" role="dialog" aria-label="Edit user">
        <div className="bar">
          <h2 style={{ margin: 0 }}>{u.email || u.uid}</h2>
          <span className="spacer" />
          <button className="sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="users-uid" style={{ marginBottom: 10 }}>
          uid {u.uid}
          {isMe && ' · this is you'}
        </div>

        {!u.hasAuth && (
          <div className="status err">
            This record has no sign-in account (it was deleted) — only its database fields can be edited.
          </div>
        )}

        {direct && (
          <div className="hint" style={{ marginTop: 0 }}>
            Password, e-mail, verified/disabled and deleting the account need the service-account key on the server
            (FIREBASE_SERVICE_ACCOUNT_FILE in .env.server). The database record below can be edited now.
          </div>
        )}
        {u.hasAuth && !direct && (
          <div className="users-grid">
            <label>E-mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <label>Display name</label>
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            <label>New password</label>
            <div className="users-row">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                autoComplete="new-password"
                placeholder="leave empty to keep the current one"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button className="sm" type="button" onClick={() => setShowPw((s) => !s)}>
                {showPw ? 'hide' : 'show'}
              </button>
              <button
                className="sm"
                type="button"
                onClick={() => {
                  setPassword(randomPassword());
                  setShowPw(true);
                }}
              >
                generate
              </button>
            </div>
            <label>Flags</label>
            <div className="users-row">
              <label className="users-check">
                <input type="checkbox" checked={emailVerified} onChange={(e) => setEmailVerified(e.target.checked)} />
                e-mail verified
              </label>
              <label className="users-check">
                <input
                  type="checkbox"
                  checked={disabled}
                  disabled={isMe}
                  onChange={(e) => setDisabled(e.target.checked)}
                />
                disabled (cannot sign in)
              </label>
            </div>
          </div>
        )}

        <h3 className="users-h3">Database record · users/{u.uid}</h3>
        <div className="users-grid">
          <label>role</label>
          <div className="users-row">
            <input
              type="text"
              list="users-roles"
              value={role}
              disabled={isMe}
              placeholder="(none)"
              onChange={(e) => setRole(e.target.value)}
            />
            <datalist id="users-roles">
              {roles.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </div>
          {(subRole || origStart || origEnd) && (
            <>
              <label title={SUB_START}>subscription start</label>
              <div className="users-row">
                <input
                  type="date"
                  value={subStart}
                  className={startMissing ? 'users-missing' : ''}
                  onChange={(e) => setSubStart(e.target.value)}
                />
                <button className="sm" type="button" onClick={() => setSubStart(ymd(new Date()))}>
                  today
                </button>
              </div>
              <label title={SUB_END}>subscription end</label>
              <div className="users-row">
                <input
                  type="date"
                  value={subEnd}
                  min={subStart || undefined}
                  className={endMissing ? 'users-missing' : ''}
                  onChange={(e) => setSubEnd(e.target.value)}
                />
                {(
                  [
                    ['+7 d', 7, 'd'],
                    ['+1 mo', 1, 'm'],
                    ['+1 y', 1, 'y'],
                  ] as const
                ).map(([label, n, unit]) => (
                  <button
                    key={label}
                    className="sm"
                    type="button"
                    title="From the start date (today if none)"
                    onClick={() => {
                      const from = subStart || ymd(new Date());
                      if (!subStart) setSubStart(from);
                      setSubEnd(addPeriod(from, n, unit));
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {(startMissing || endMissing) && (
                <>
                  <span />
                  <div className="users-missing-note">
                    {roleChanged
                      ? `Set the start and end of the new "${role.trim()}" period — reminders go by these dates.`
                      : `A "${role.trim()}" user needs both dates — reminders go by them.`}
                  </div>
                </>
              )}
            </>
          )}
          {fields.map((f, i) => (
            <React.Fragment key={i}>
              <input
                type="text"
                value={f.key}
                placeholder="field name"
                onChange={(e) => patchField(i, { key: e.target.value })}
              />
              <div className="users-row">
                <select
                  value={f.type}
                  onChange={(e) => {
                    const type = e.target.value as FieldType;
                    patchField(i, { type, value: type === 'boolean' ? String(f.value === 'true') : f.value });
                  }}
                  style={{ maxWidth: 90 }}
                >
                  <option value="string">text</option>
                  <option value="number">number</option>
                  <option value="boolean">true/false</option>
                </select>
                {f.type === 'boolean' ? (
                  <select value={f.value} onChange={(e) => patchField(i, { value: e.target.value })}>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input type="text" value={f.value} onChange={(e) => patchField(i, { value: e.target.value })} />
                )}
                <button
                  className="sm danger"
                  type="button"
                  title="Remove this field"
                  onClick={() => setFields((prev) => prev.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>
        <button
          className="sm"
          type="button"
          style={{ marginTop: 6 }}
          onClick={() => setFields((prev) => [...prev, { key: '', type: 'string', value: '' }])}
        >
          + Add field
        </button>
        {u.nested.length > 0 && (
          <div className="hint">Nested data kept as is: {u.nested.join(', ')}.</div>
        )}

        {err && (
          <div className="status err" style={{ marginTop: 10 }}>
            {err}
          </div>
        )}
        <div className="bar" style={{ marginTop: 14 }}>
          <button className="primary sm" disabled={!live || busy} onClick={submit}>
            {busy ? 'Saving…' : live ? 'Save changes' : 'Save (PRODUCTION only)'}
          </button>
          <button className="sm" onClick={onClose}>
            Cancel
          </button>
          <span className="spacer" />
          {!isMe && !direct && (
            <>
              <label className="users-check" style={{ margin: 0 }}>
                <input type="checkbox" checked={deleteData} onChange={(e) => setDeleteData(e.target.checked)} />
                also delete users/ record
              </label>
              <button className="sm danger" disabled={!live} onClick={() => onDelete(deleteData)}>
                🗑 Delete user
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersView;
